import { apiClient } from '../api/client';
import { Product } from '@/types';
import { categoryService } from './category.service';
import { SEM_SERVIDOR, ehEntregaDigital, servidorNaApi, type TipoDeEntrega } from '@/lib/loja-digital';

export interface ProductAPI {
  id: string;
  name: string;
  slug: string;
  shortDescription?: string;
  description: string;
  price: number;
  promotionalPrice?: number | null;
  imageUrl?: string;
  categoryId: string;
  isActive: boolean;
  isFeatured?: boolean;
  stock?: number;
  order: number;
  durationDays?: number | null;
  minQuantity?: number | null;
  purchaseLimitPerUser?: number | null;
  /** Servidor de entrega: é o que monta o link `/loja/:serverId/...`. */
  serverId?: string | null;
  /** COMMAND (no jogo) ou FILE, LICENSE_KEY, CONTENT (produto digital). */
  deliveryKind?: TipoDeEntrega;
  downloadLimit?: number | null;
  /** Chave de licença sem estoque. */
  soldOut?: boolean;
}

export const productService = {
  async listByStore(storeId: string, categoryId?: string, serverId?: string): Promise<Product[]> {
    const searchParams = new URLSearchParams();
    if (categoryId) searchParams.set('categoryId', categoryId);
    const servidor = servidorNaApi(serverId);
    if (servidor) searchParams.set('serverId', servidor);
    const query = searchParams.toString();
    const [productsResponse, categories] = await Promise.all([
      apiClient.get<{ products: ProductAPI[]; total: number }>(
        `/public/stores/${storeId}/products${query ? `?${query}` : ''}`
      ),
      categoryService.listByStore(storeId, undefined, serverId),
    ]);
    
    // Criar map de categoryId -> categorySlug para lookup rápido
    const categoryMap = new Map(categories.map(cat => [cat.id, cat.slug]));
    
    // Mapear para o formato esperado pelo frontend
    return (productsResponse.products || []).map((prod) => ({
      id: prod.id,
      categoryId: prod.categoryId,
      categorySlug: categoryMap.get(prod.categoryId) ?? '',
      name: prod.name,
      slug: prod.slug,
      shortDescription: prod.shortDescription,
      description: prod.description,
      price: prod.promotionalPrice || prod.price, // Se tem promoção, mostra promo como preço atual
      originalPrice: prod.promotionalPrice ? prod.price : null, // Se tem promoção, preço normal vira original
      image: prod.imageUrl || 'https://placehold.co/600x400?text=Produto',
      benefits: [],
      duration: prod.durationDays ? `${prod.durationDays} dias` : 'Permanente',
      durationDays: prod.durationDays,
      minQuantity: prod.minQuantity ?? undefined,
      purchaseLimitPerUser: prod.purchaseLimitPerUser ?? undefined,
      featured: prod.isFeatured ?? false,
      // Produto digital não tem servidor: o link usa o segmento fixo da loja digital.
      serverId: ehEntregaDigital(prod.deliveryKind) ? SEM_SERVIDOR : prod.serverId ?? null,
      deliveryKind: prod.deliveryKind ?? 'COMMAND',
      downloadLimit: prod.downloadLimit ?? null,
      soldOut: prod.soldOut ?? false,
    }));
  },

  async getBySlug(storeId: string, slug: string, serverId?: string): Promise<Product | null> {
    const products = await this.listByStore(storeId, undefined, serverId);
    return products.find((p) => p.slug === slug) || null;
  },
};
